import * as THREE from 'three';
import { getMeshElementType } from '../../../utils/meshDataHelpers';

/**
 * Finds the nearest wall in a specified direction from a point
 * @param {THREE.Vector3} point - The starting point
 * @param {THREE.Vector3} direction - The direction vector (should be normalized)
 * @param {Array} walls - Array of wall objects from the JSON data
 * @param {THREE.Scene} scene - The Three.js scene (used for world matrix calculations)
 * @param {Number} maxDistance - Maximum distance to check for walls (optional, default: 100)
 * @returns {Object|null} The nearest wall and distance, or null if no wall found
 */

export const findNearestWallInDirection = (
  point,
  direction,
  walls,
  scene,
  maxDistance = 100
) => {
  // Ensure direction is normalized
  const dir = direction.clone().normalize();

  // Create a raycaster from the point in the specified direction
  const raycaster = new THREE.Raycaster(point, dir, 0, maxDistance);

  // Create wall meshes for intersection testing
  const wallMeshes = walls.map((wall, index) => {
    // Get dimensions - handle both array and object format
    let width, height, depth;
    if (Array.isArray(wall.dimensions)) {
      width = wall.dimensions[0];
      height = wall.dimensions[1];
      depth = wall.dimensions[2] ?? 0.005;
    } else if (wall.dimensions && typeof wall.dimensions === 'object') {
      width = wall.dimensions.x || 1;
      height = wall.dimensions.y || 2.5;
      depth = wall.dimensions.z || 0.005;
    } else {
      // Default dimensions
      width = 1;
      height = 2.5;
      depth = 0.005;
    }

    // Create a temporary wall mesh
    const wallGeometry = new THREE.BoxGeometry(width, height, depth);

    const wallMaterial = new THREE.MeshBasicMaterial({
      visible: false, // No need to render, just for intersection
    });

    const wallMesh = new THREE.Mesh(wallGeometry, wallMaterial);

    // Apply wall position and rotation from center/rotation
    if (wall.center) {
      wallMesh.position.set(
        wall.center.x || 0,
        wall.center.y || 0,
        wall.center.z || 0
      );
    }

    if (wall.rotation) {
      wallMesh.rotation.set(
        wall.rotation.x || 0,
        wall.rotation.y || 0,
        wall.rotation.z || 0
      );
    }

    // Add to scene temporarily to update world matrix
    scene.add(wallMesh);
    scene.updateMatrixWorld(true);

    // Track wall index using the mesh's name (avoid userData for data model properties)
    wallMesh.name = `temp_wall_${index}`;

    return wallMesh;
  });

  // Find intersections
  const intersections = raycaster.intersectObjects(wallMeshes);

  // Clean up temporary meshes
  wallMeshes.forEach((mesh) => {
    scene.remove(mesh);
    mesh.geometry.dispose();
    mesh.material.dispose();
  });

  // If we found any intersections, return the closest one
  if (intersections.length > 0) {
    const closestIntersection = intersections[0];
    // Extract wall index from mesh name
    const wallIndex = parseInt(
      closestIntersection.object.name.replace('temp_wall_', ''),
      10
    );
    const hitMesh = closestIntersection.object;

    // Get the face normal in local space
    const normalLocal = closestIntersection.face.normal.clone();

    // Transform the normal to world space
    const normalWorld = normalLocal
      .clone()
      .transformDirection(hitMesh.matrixWorld);

    return {
      wall: walls[wallIndex],
      distance: closestIntersection.distance,
      point: closestIntersection.point, // Intersection point
      normal: normalWorld, // Properly transformed normal
      wallIndex: wallIndex,
    };
  }

  // No wall found in the specified direction
  return null;
};

export const findFloorYPosition = (position, scene) => {
  console.log('🔍 Finding floor Y position for:', {
    inputPosition: { x: position.x, y: position.y, z: position.z },
  });

  // Default fallback if no floor is found
  let floorY = 0;

  // Find all floor objects in the scene
  const floorObjects = [];
  scene.traverse((child) => {
    if (getMeshElementType(child, null) === 'floor') {
      floorObjects.push(child);
      console.log('  Found floor object:', {
        name: child.name,
        position: child.position.clone(),
        uuid: child.uuid,
      });
    }
  });

  console.log(`  Total floor objects found: ${floorObjects.length}`);

  if (floorObjects.length === 0) {
    console.log('  ⚠️ No floor objects found in scene, returning default Y: 0');
    return floorY;
  }

  // Create a raycaster pointing down from the position
  const rayStart = new THREE.Vector3(position.x, position.y + 1, position.z);
  const rayDirection = new THREE.Vector3(0, -1, 0);

  console.log('  📐 Raycaster setup:', {
    start: { x: rayStart.x, y: rayStart.y, z: rayStart.z },
    direction: { x: rayDirection.x, y: rayDirection.y, z: rayDirection.z },
    maxDistance: 10,
  });

  const raycaster = new THREE.Raycaster(
    rayStart,
    rayDirection,
    0, // Near
    10 // Far - max distance to search
  );

  // Find intersections with floor objects
  const intersections = raycaster.intersectObjects(floorObjects, true); // true for recursive

  console.log(`  🎯 Found ${intersections.length} floor intersections`);

  // If we found intersections, use the closest one
  if (intersections.length > 0) {
    // The y position is where the ray hit the floor
    floorY = intersections[0].point.y;
    console.log('  ✅ Floor found at Y:', floorY, {
      intersectionPoint: intersections[0].point.clone(),
      distance: intersections[0].distance,
      objectName: intersections[0].object.name,
    });
  } else {
    console.log('  ❌ No floor intersection found! Using default Y: 0');
  }

  console.log('  📍 Final floor Y position:', floorY);
  return floorY;
};
